import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  capitalize,
  PIN_CODE_LENGTH,
  RFIDPolicy,
  RoleTypes,
} from "../../../helpers/constants";
import {
  Box,
  Button,
  FormHelperText,
  IconButton,
  Input,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Theme,
  Typography,
} from "@material-ui/core";
import makeStyles from "@material-ui/core/styles/makeStyles";
import createStyles from "@material-ui/core/styles/createStyles";
import {
  IEventResolution,
  IUserDetails,
} from "../../../services/header.services";
import { Visibility, VisibilityOff } from "@material-ui/icons";

import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormControl from "@material-ui/core/FormControl";
import FormLabel from "@material-ui/core/FormLabel";
import PinValidation from "../../common/PinValidation";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../helpers/store";
import {
  resolveEventWithPassword,
  resolveEventWithPin,
} from "../../app/asyncThunkActions";
import { useAppSelector } from "../../app/appHooks";
import clsx from "clsx";
import YesNoDialog from "../../common/YesNoDialog";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formContainer: {
      display: "flex",
      flexDirection: "column",
      maxWidth: "440px",
      padding: theme.spacing(3),
      backgroundColor: theme.palette.light.light,
    },
    resolutionOption: {
      alignItems: "flex-start",
      marginBottom: theme.spacing(2),
    },
    resolutionRadio: {
      paddingTop: theme.spacing(0.5),
    },
    resolutionDescription: {
      color: theme.palette.text.secondary,
      fontSize: theme.typography.body2.fontSize,
      lineHeight: 1.4,
      marginTop: theme.spacing(0.5),
    },
    commentContainer: {
      marginTop: theme.spacing(1),
    },
    pinValidation: {
      margin: 0,
    },
    orText: {
      lineHeight: 1,
      marginTop: theme.spacing(-1),
    },
    buttonRoot: {
      minWidth: "110px",
    },
    popoverContent: {
      flex: 1,
      minHeight: "150px",
      overflowY: "auto",
    },
    responderContainer: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    },
    listHeader: {
      backgroundColor: theme.palette.light.light,
    },
    popoverTitle: {
      color: theme.palette.light.light,
      backgroundColor: theme.palette.primary.main,
      display: "flex",
      justifyContent: "center",
      padding: theme.spacing(2, 0),
    },
    popoverTitleWarn: {
      backgroundColor: theme.palette.warning.main,
    },
    popoverTitleBlock: {
      backgroundColor: theme.palette.error.main,
    },
    disabledText: {
      color: theme.palette.text.disabled,
    },
    bold: {
      fontWeight: "bolder",
    },
    buttonCancel: {
      border: `2px solid ${theme.palette.dark.main}`,
    },
    containedPrimary: {
      color: theme.palette.light.light,
    },
    rfidTextBox: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      paddingBottom: theme.spacing(2),
    },
    rfidText: {
      textAlign: "center",
      fontSize: "0.80rem",
    },
    rfidBlock: {
      color: theme.palette.error.main,
    },
    rfidWarn: {
      color: theme.palette.warning.main,
    },
  })
);

interface FormState {
  resolution: string;
  comment: string;
  pinCode: string;
  password: string;
}

export const ResolveNotification = (props: IProps) => {
  const {
    onClose,
    eventID,
    currentUser,
    resolutionID,
    eventResolutions,
    fromDashboard,
    fromNotifications,
    blockResolution,
    warnResolution,
  } = props;

  /* ---- State ---- */
  const [values, setValues] = useState<FormState>({
    resolution: "",
    comment: "",
    pinCode: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState("password");
  const [pinError, setPinError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [dialogToggle, setDialogToggle] = useState(false);

  const community = useAppSelector(
    (state) => state.headerState.selectedCommunity
  );
  const rfidPolicy = community?.rfid_policy || RFIDPolicy.DISABLED;
  const isCommunityAdmin = !!community?.role.includes(RoleTypes.admin);
  let isOrgAdmin = false;
  if (community) {
    isOrgAdmin = community.role.includes(RoleTypes.org);
  }
  const isAdmin = isCommunityAdmin || isOrgAdmin;

  /* ---- Hooks ---- */
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();

  /* ---- References ---- */
  const passwordRef = useRef(document.createElement("input"));

  /* ---- Effects ---- */
  // Set cursor when showing/hiding passwords
  useEffect(() => {
    // Moving cursor to the end
    if (passwordRef.current && passwordRef.current.value) {
      passwordRef.current.selectionStart = passwordRef.current.value.length;
      passwordRef.current.selectionEnd = passwordRef.current.value.length;
    }
  }, [showPassword]);

  // Initialize resolution when events is resolved
  useEffect(() => {
    if (resolutionID && resolutionID !== "") {
      const initialResolution = {
        resolution: resolutionID,
        comment: "",
        pinCode: "",
        password: "",
      };
      setValues(initialResolution);
    }
  }, [resolutionID]);

  const listTitle = `${t("resolve")} ${t("notification")} `;
  const resolvedIsEnabled =
    values.resolution !== "" &&
    ((values.pinCode !== "" && values.pinCode.length === PIN_CODE_LENGTH) ||
      values.password !== "");

  /* ---- Methods ---- */
  const toggleShowPassword = useCallback(() => {
    setShowPassword((current) =>
      showPassword === "text" ? "password" : "text"
    );
    // Setting focus here
    if (passwordRef.current) {
      passwordRef.current.focus();
    }
  }, [showPassword, passwordRef]);

  const handleChange = (prop: keyof FormState) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setValues({ ...values, [prop]: event.target.value });
    setPasswordError("");
    setPinError("");
  };

  const handleChangePinCode = (value: string) => {
    setValues({ ...values, pinCode: value });
    setPinError("");
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const handleClose = () => {
    setDialogToggle(false);
  };

  const handleSubmit = async (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    const eventNote = values.comment.trim() || undefined;
    if (eventID && currentUser?.email && values.pinCode !== "") {
      if (values.pinCode.length === PIN_CODE_LENGTH) {
        const resolveData = {
          resolveEventData: {
            email: currentUser.email,
            pin: values.pinCode,
            eventID: eventID,
            resolutionID: values.resolution,
            eventNote,
          },
          fromDashboard: fromDashboard,
          fromNotifications: fromNotifications,
        };
        const resultAction = await dispatch(resolveEventWithPin(resolveData));
        if (resolveEventWithPin.fulfilled.match(resultAction)) {
          // if request finished correctly, close modal
          onClose();
          return;
        } else if (typeof resultAction.payload === "boolean") {
          // display pin error
          setPinError("PIN_is_incorrect");
          return;
        }
      } else {
        // display pin error
        setPinError("PIN_is_incorrect");
        return;
      }
    } else if (eventID && currentUser?.email && values.password !== "") {
      const resolveData = {
        resolveEventData: {
          email: currentUser.email,
          password: values.password,
          eventID: eventID,
          resolutionID: values.resolution,
          eventNote,
        },
        fromDashboard: fromDashboard,
        fromNotifications: fromNotifications,
      };

      const resultAction = await dispatch(
        resolveEventWithPassword(resolveData)
      );
      if (resolveEventWithPassword.fulfilled.match(resultAction)) {
        // if request finished correctly, close modal
        onClose();
        return;
      } else if (typeof resultAction.payload === "boolean") {
        // display pin error
        setPasswordError("password_is_incorrect");
        return;
      }
    }
  };

  const handleResolveClick = () => {
    if (warnResolution || blockResolution) {
      setDialogToggle(true);
    } else {
      handleSubmit();
    }
  };

  const isBlocked = blockResolution && !isAdmin;
  const isEnabled = resolvedIsEnabled && !isBlocked;

  return (
    <React.Fragment>
      <Box
        className={clsx(classes.popoverTitle, {
          [classes.popoverTitleWarn]: warnResolution,
          [classes.popoverTitleBlock]: blockResolution,
        })}
        fontWeight="fontWeightBold"
        fontSize="large"
      >
        {listTitle}
      </Box>
      <Box className={classes.popoverContent}>
        <form className={classes.formContainer} autoComplete="off">
          <FormControl component="fieldset" disabled={isBlocked}>
            <FormLabel>
              <Box
                marginBottom={1.5}
                color="text.primary"
                fontWeight="bolder"
                className={isBlocked ? classes.disabledText : undefined}
              >
                {t("this_notification_was")}
              </Box>
            </FormLabel>
            <RadioGroup
              aria-label="event-resolution"
              name="event-resolution"
              value={values.resolution}
              onChange={handleChange("resolution")}
            >
              {eventResolutions?.map((resolution) => {
                return (
                  <FormControlLabel
                    key={resolution.id}
                    value={resolution.id}
                    className={classes.resolutionOption}
                    control={
                      <Radio
                        color="primary"
                        className={classes.resolutionRadio}
                      />
                    }
                    color="primary"
                    label={
                      <React.Fragment>
                        <Box fontWeight="bolder">
                          {resolution.name
                            ? t(`${resolution.name}_description`)
                            : ""}
                        </Box>
                        {resolution.name && (
                          <Typography
                            className={clsx(classes.resolutionDescription, {
                              [classes.disabledText]: isBlocked,
                            })}
                          >
                            {t(`${resolution.name}_resolution_description`)}
                          </Typography>
                        )}
                      </React.Fragment>
                    }
                  />
                );
              })}
            </RadioGroup>
          </FormControl>
          <FormControl
            fullWidth
            disabled={isBlocked}
            className={classes.commentContainer}
          >
            <Box
              marginBottom={0.5}
              fontWeight="bolder"
              className={isBlocked ? classes.disabledText : undefined}
            >
              {t("comment")}
            </Box>
            <OutlinedInput
              id="resolve-comment"
              value={values.comment}
              onChange={handleChange("comment")}
              placeholder={t("enter_comment")}
              multiline={true}
              notched={false}
              autoComplete="off"
              rows="3"
            />
          </FormControl>
          <Typography
            variant="subtitle1"
            className={isBlocked ? classes.disabledText : undefined}
          >
            <Box marginTop={1} fontWeight="bolder">
              {currentUser ? currentUser.email : ""}
            </Box>
          </Typography>

          <PinValidation
            onChange={handleChangePinCode}
            error={pinError}
            isBlocked={isBlocked}
            className={classes.pinValidation}
          />
          <Box margin="auto">
            <Typography
              variant="subtitle1"
              className={clsx(classes.orText, {
                [classes.disabledText]: isBlocked,
              })}
            >
              {capitalize(`${t("or")}`)}
            </Typography>
          </Box>
          <Box
            marginTop={-1}
            marginBottom="16px"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <FormControl error={passwordError !== ""} disabled={isBlocked}>
              <InputLabel htmlFor="standard-adornment-password">
                {t("password")}
              </InputLabel>
              <Input
                id="standard-adornment-password"
                type={showPassword}
                value={values.password}
                onChange={handleChange("password")}
                inputRef={passwordRef}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={toggleShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      disabled={isBlocked}
                    >
                      {showPassword === "text" ? (
                        <Visibility />
                      ) : (
                        <VisibilityOff />
                      )}
                    </IconButton>
                  </InputAdornment>
                }
              />
              <FormHelperText id="password-error-helper">
                {passwordError !== "" ? t(passwordError) : ""}
              </FormHelperText>
            </FormControl>
          </Box>
          <Box className={classes.rfidTextBox}>
            {rfidPolicy === RFIDPolicy.BLOCK && blockResolution && (
              <Typography className={clsx(classes.rfidText, classes.rfidBlock)}>
                {t("rfid_scan_required")}
              </Typography>
            )}

            {rfidPolicy === RFIDPolicy.WARN && warnResolution && (
              <Typography className={clsx(classes.rfidText, classes.rfidWarn)}>
                {t("rfid_scan_warning")}
              </Typography>
            )}
          </Box>
          <Box display="flex" justifyContent="space-around">
            <Button
              id="cancel-button"
              classes={{
                root: classes.buttonRoot,
                outlined: classes.buttonCancel,
              }}
              color="default"
              variant="outlined"
              onClick={onClose}
            >
              {t("cancel")}
            </Button>
            <Button
              id="submit"
              disabled={!isEnabled}
              classes={{
                root: classes.buttonRoot,
                containedPrimary: classes.containedPrimary,
              }}
              color="primary"
              variant={!isEnabled ? "outlined" : "contained"}
              onClick={handleResolveClick}
            >
              {t("resolve")}
            </Button>
          </Box>
        </form>
      </Box>
      <YesNoDialog
        toggle={dialogToggle}
        title={t("resolve_no_scan_title")}
        message={"resolve_no_scan_description"}
        possitiveText={"resolve"}
        negativeText={"cancel"}
        positiveAction={() => {
          setDialogToggle(false);
          handleSubmit();
        }}
        negativeAction={() => {
          return;
        }}
        onClose={handleClose}
      />
    </React.Fragment>
  );
};

interface IProps {
  currentUser?: IUserDetails;
  resolutionID?: string | null;
  eventResolutions?: IEventResolution[];
  eventID?: string;
  fromDashboard: boolean;
  fromNotifications: boolean;
  blockResolution?: boolean;
  warnResolution?: boolean;
  onClose: () => void;
}
